import { Behavior, BehaviorConfig } from './Behavior';
import { Entity } from '../base/Entity';
import { SpecialState } from '../base/Unit';
import {
  WeightClass,
  WeightSpeeds,
  WeightValues,
  AbductionConfig,
} from '../../constants/enums';
import { DataManagerPowerUp } from '../../data/DataManagerPowerUp';
import { EffectResolver } from '../../systems/EffectResolver';
import { Player } from '../Player';

export { WeightClass };

export interface AbductableConfig extends BehaviorConfig {
  bioMatter?: number;
  weight?: WeightClass;
  abductionSpeedMultiplier?: number;
  /** If true, entity can be abducted even while dying (death animation playing) */
  canAbductWhileDying?: boolean;
}

export interface AbductionEvent {
  entity: Entity;
  bioMatter: number;
  weight: WeightClass;
  /** Custom notification text (powerups, etc.) */
  notificationText?: string;
  /** Notification type: 'biomatter' | 'powerup' | 'health' */
  notificationType?: string;
}

/**
 * AbductableBehavior - Allows entity to be abducted by player
 *
 * Used by: Cow, Human, Bat, PowerUp, etc.
 *
 * IMPORTANT: Entities with AbductableBehavior MUST also have PlatformBehavior.
 * When abduction is cancelled, PlatformBehavior handles the falling/landing.
 * This behavior only manages the abduction state, not gravity/falling.
 *
 * Features:
 * - Bio matter value
 * - Weight affects abduction speed
 * - Progress tracking
 * - Callbacks for abduction stages
 */
export class AbductableBehavior extends Behavior {
  public bioMatter: number;
  public weight: WeightClass;
  public isBeingAbducted: boolean = false;
  public abductionProgress: number = 0;
  public abductionSpeed: number;
  /** Tractor power from TractorBeamBehavior (affects lift speed) */
  public tractorPower: number = 100;
  /** If true, entity can be abducted even while dying */
  public canAbductWhileDying: boolean = false;

  private originalY: number;
  private originalGravity: boolean = true;

  constructor(entity: Entity, config: AbductableConfig = {}) {
    super(entity, config);

    // LOUD FAIL: AbductableBehavior requires PlatformBehavior for gravity/falling
    // PlatformBehavior sets entity.state.behaviors.platform in its constructor
    // We check after a short delay to allow PlatformBehavior to initialize first
    this.scene.time.delayedCall(0, () => {
      if (!this.entity.state.behaviors.platform) {
        console.error('[AbductableBehavior] Entity MUST have PlatformBehavior', {
          entity: entity.texture?.key,
          fix: 'Add behaviors.platform to entity JSON (required for gravity/falling)',
        });
      }
    });

    this.bioMatter = config.bioMatter ?? AbductionConfig.DefaultBioMatter;
    this.weight = config.weight ?? WeightClass.Light;
    // Default to true - entities can be abducted while playing death animation
    this.canAbductWhileDying = config.canAbductWhileDying ?? true;

    const baseSpeed = WeightSpeeds[this.weight];
    this.abductionSpeed = baseSpeed * (config.abductionSpeedMultiplier ?? 1);

    this.originalY = entity.y;

    // Initialize abduction state for state-based communication with other behaviors
    this.entity.state.behaviors.abductable = { state: 'idle' };
  }

  /**
   * Update abduction state
   * NOTE: Falling/gravity is handled by PlatformBehavior, not here
   */
  update(time: number, delta: number): void {
    // Only handle abduction - PlatformBehavior handles gravity/falling
    if (!this.isBeingAbducted) return;

    const deltaSeconds = delta / 1000;

    // C3 formula: liftSpeed = (3 * tractorPower) / weightValue
    // weightValue: light=1, medium=2, heavy=4
    const weightValue = WeightValues[this.weight];
    const liftSpeed = (3 * this.tractorPower) / weightValue;

    // Move entity upward (toward UFO)
    this.entity.y -= liftSpeed * deltaSeconds;

    // Completion is handled by TractorBeamBehavior via physics overlap with player
  }

  /**
   * Start being abducted
   * @param tractorPower - Power from tractor beam (affects lift speed via C3 formula)
   */
  startAbduction(tractorPower?: number): void {
    if (this.isBeingAbducted) return;

    this.tractorPower = tractorPower ?? 100;
    this.isBeingAbducted = true;
    this.abductionProgress = 0;
    this.originalY = this.entity.y;

    // Update abduction state for state-based communication
    this.entity.state.behaviors.abductable!.state = 'lifting';

    // Update entity special state to abducted
    this.updateEntitySpecialState('abducted');

    // Disable gravity while being abducted
    if (this.entity.body) {
      const body = this.entity.body as Phaser.Physics.Arcade.Body;
      this.originalGravity = body.allowGravity;
      body.setAllowGravity(false);
      body.setVelocity(0, 0);
    }

    // Stop all tweens on entity (float/pulse animations)
    this.scene.tweens.killTweensOf(this.entity);

    // Emit event
    this.scene.events.emit('abductionStarted', {
      entity: this.entity,
      bioMatter: this.bioMatter,
      weight: this.weight
    } as AbductionEvent);
  }

  /**
   * Cancel abduction (e.g., player moved away)
   * Restores gravity - PlatformBehavior will handle the falling
   */
  cancelAbduction(): void {
    if (!this.isBeingAbducted) return;

    this.isBeingAbducted = false;
    this.abductionProgress = 0;

    // Update abduction state - prevents re-abduction until landing
    this.entity.state.behaviors.abductable!.state = 'released';

    // Update entity special state to falling (for animations)
    this.updateEntitySpecialState('falling');

    // Restore gravity - PlatformBehavior will handle falling and landing
    if (this.entity.body) {
      const body = this.entity.body as Phaser.Physics.Arcade.Body;
      body.setAllowGravity(this.originalGravity);
      // Don't set velocity - let PlatformBehavior apply gravity naturally
    }

    // Emit event
    this.scene.events.emit('abductionCancelled', {
      entity: this.entity,
      bioMatter: this.bioMatter,
      weight: this.weight
    } as AbductionEvent);
  }

  /**
   * Complete the abduction
   */
  completeAbduction(): void {
    this.isBeingAbducted = false;

    // Get powerup definition from data-driven system
    const prefabName = (this.entity as Entity & { prefabName?: string }).prefabName || this.entity.texture?.key;
    const powerupDef = prefabName ? DataManagerPowerUp.get(prefabName) : undefined;

    let notificationText: string | undefined;
    let notificationType: string | undefined;

    if (powerupDef) {
      // Apply effects from JSON
      const player = this.scene.registry.get('player') as Player | undefined;
      if (player && powerupDef.effects.length > 0) {
        EffectResolver.applyAll(this.scene, player, powerupDef.effects);
      }

      // Generate notification from template
      notificationText = DataManagerPowerUp.getNotificationText(prefabName!, powerupDef.effects);
      notificationType = powerupDef.bioMatter > 0 ? 'biomatter' : 'powerup';

      // Use bioMatter from JSON
      this.bioMatter = powerupDef.bioMatter;
    }

    // Emit event
    this.scene.events.emit('entityAbducted', {
      entity: this.entity,
      bioMatter: this.bioMatter,
      weight: this.weight,
      notificationText,
      notificationType,
    } as AbductionEvent);

    // Destroy entity
    this.entity.destroy();
  }

  /**
   * Get abduction progress as percentage
   */
  getProgressPercent(): number {
    return Math.min(100, this.abductionProgress * 100);
  }

  /**
   * Check if can be abducted
   */
  canBeAbducted(): boolean {
    // Cannot abduct if already being abducted
    if (this.isBeingAbducted) {
      return false;
    }

    // Cannot abduct while flying (FlyerBehavior sets entity.state.behaviors.flyer.isFlying)
    if (this.entity.state.behaviors.flyer?.isFlying) {
      return false;
    }

    // Cannot re-abduct while falling after being released from abduction
    // (prevents instant re-grab, must wait until entity lands)
    const abductState = this.entity.state.behaviors.abductable?.state;
    const isGrounded = this.entity.state.behaviors.platform?.isGrounded;
    if (abductState === 'released' && !isGrounded) {
      return false;
    }

    // If entity is fully destroyed/inactive, cannot abduct
    if (!this.entity.active) {
      return false;
    }

    return true;
  }

  /**
   * Set bio matter value
   */
  setBioMatter(value: number): void {
    this.bioMatter = value;
  }

  /**
   * Set weight class
   */
  setWeight(weight: WeightClass): void {
    this.weight = weight;
    this.abductionSpeed = WeightSpeeds[weight];
  }

  /**
   * Update entity's special state if it supports the state system
   * (Unit has setSpecial, other entities may not)
   */
  private updateEntitySpecialState(state: SpecialState): void {
    if (typeof (this.entity as Entity & { setSpecial?: (state: SpecialState) => void }).setSpecial === 'function') {
      (this.entity as Entity & { setSpecial: (state: SpecialState) => void }).setSpecial(state);
    }
  }

}
